/* eslint-disable no-underscore-dangle */
import * as React from 'react'
import {
  ColDef,
  CellClassParams,
  GetContextMenuItemsParams,
  DefaultMenuItem,
  MenuItemDef,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-enterprise'
import { AG_GRID_LOCALE_FI } from '@ag-grid-community/locale'
import i18next from 'i18next'
import { EnhancedStatementRow, FinancialRowType } from './dataEnhancement'
import { FinancialStatementRowAction, StatementColumn } from '../types'
import { HeaderCellRenderer } from '../components/StatementTable/cellRenderers/HeaderCellRenderer'
import { ExpandAllHeaderRenderer } from '../../../shared/components/custom/Grid/cellRenderers'
import {
  HeadingGroupCell,
  AccountGroupCell,
  DimensionGroupCell,
  DimensionGroupGroupCell,
  InvestmentMenuGroupCell,
  AmendmentMenuGroupCell,
  RowActionsGroupCell
  // DriverGroupCell
} from '../components/StatementTable/cellRenderers/GroupCellRenderers'
import { formatVariableValue } from '../../../pages/reporting/custom/components/grid/utils/valueFormatting'
import { NumberFormatConfig } from '../../../shared/utils/numberScale'
import { VariableType } from '../../../components/Table/types'
import { extractDataValue } from '../../../shared/utils/balanceRegistry'
import { vouchersConfig } from '../../../config/integrations/vouchers'
import { TaskApplication } from '../../../pages/superuser/types'

/**
 * Centralized mapping of row types to CSS class names
 */
export const ROW_TYPE_TO_CSS_CLASS: Record<FinancialRowType, string> = {
  [FinancialRowType.ACCOUNT]: 'account-row',
  [FinancialRowType.AMENDMENT_MENU]: 'amendment-menu-row',
  [FinancialRowType.AMENDMENT]: 'amendment-row',
  [FinancialRowType.COUNTER_AMENDMENT_MENU]: 'counter-amendment-menu-row',
  [FinancialRowType.DIMENSION_GROUP]: 'dimension-group-row',
  [FinancialRowType.DIMENSION]: 'dimension-row',
  [FinancialRowType.HEADING]: 'heading-row',
  [FinancialRowType.INVESTMENT_MENU]: 'investment-menu-row',
  [FinancialRowType.INVESTMENT]: 'investment-row',
  [FinancialRowType.REALLOCATION_MENU]: 'reallocation-menu-row',
  [FinancialRowType.REALLOCATION]: 'reallocation-row'
}

/**
 * Get CSS class name for a row type
 */
export const getRowTypeClassName = (rowType: FinancialRowType): string => {
  return ROW_TYPE_TO_CSS_CLASS[rowType] || ''
}

// Common cell class rules for row types
export const getBaseRowTypeCellClassRules = () => ({
  bold: (params: CellClassParams<EnhancedStatementRow>) => params.data?.isParent === true,
  // Generate class rules dynamically from the mapping
  ...Object.entries(ROW_TYPE_TO_CSS_CLASS).reduce(
    (acc, [rowType, className]) => ({
      ...acc,
      [className]: (params: CellClassParams<EnhancedStatementRow>) => params.data?.rowType === rowType
    }),
    {}
  )
})

/**
 * Check if vouchers are available for the given row and column
 */
export const isVoucherAvailable = (
  row: EnhancedStatementRow | undefined,
  column: StatementColumn,
  accountingSoftware?: TaskApplication
): boolean => {
  if (!row || !column.dataType) return false
  if (column.dataType !== 'actuals') return false
  if (!accountingSoftware || !vouchersConfig[accountingSoftware]) return false

  // Don't allow vouchers for parent rows (bold rows)
  if (row.isParent === true) return false

  const allowedRowTypes = ['HEADING', 'ACCOUNT', 'DIMENSION']
  return allowedRowTypes.includes(row.rowType)
}

/**
 * Creates context menu items callback with voucher support and driver data navigation
 */
export const createContextMenuItems = (
  onShowVouchers?: (row: EnhancedStatementRow, column: StatementColumn) => void,
  onNavigateToDriverData?: (row: EnhancedStatementRow) => void,
  accountingSoftware?: TaskApplication
) => {
  return (params: GetContextMenuItemsParams<EnhancedStatementRow>): (DefaultMenuItem | MenuItemDef)[] => {
    const defaultItems: DefaultMenuItem[] = ['copy', 'copyWithHeaders']

    if (!params.node?.data) {
      return defaultItems
    }

    const row = params.node.data
    const menuItems: (MenuItemDef | DefaultMenuItem)[] = []

    // Add driver data menu item if row has driver-based budgeting method
    if (onNavigateToDriverData && row.budgetingMethod?.method === 'driver') {
      const driverDataMenuItem: MenuItemDef = {
        name: i18next.t('financialStatementsPage:row-drivers', 'Rivin ajurit'),
        action: () => {
          onNavigateToDriverData(row)
        },
        icon: '<i class="ag-icon-edit" aria-hidden="true"></i>'
      }
      menuItems.push(driverDataMenuItem)
    }

    // Add voucher menu item if available
    if (onShowVouchers && params.column) {
      const columnData = (params.column.getColDef().headerComponentParams as any)?.columnData as
        | StatementColumn
        | undefined

      if (columnData && isVoucherAvailable(row, columnData, accountingSoftware)) {
        const voucherMenuItem: MenuItemDef = {
          name: i18next.t('global:vouchers'),
          action: () => {
            onShowVouchers(row, columnData)
          },
          icon: '<i class="fa fa-file-text-o" aria-hidden="true"></i>'
        }
        menuItems.push(voucherMenuItem)
      }
    }

    // Add separator if we have any custom items
    if (menuItems.length > 0) {
      menuItems.push('separator' as DefaultMenuItem)
    }

    return [...menuItems, ...defaultItems]
  }
}

/**
 * Options for creating auto group column definition
 */

export interface AutoGroupColumnDefOptions {
  localeText?: { [key: string]: string }
  onRowAction?: (rowData: EnhancedStatementRow, action: FinancialStatementRowAction) => void
}

/**
 * Creates auto group column definition for tree data
 */
export const createAutoGroupColumnDef = (options: AutoGroupColumnDefOptions = {}): ColDef<EnhancedStatementRow> => {
  const { localeText, onRowAction } = options
  return {
    field: 'name',
    headerName: i18next.t('global:name'),
    headerComponent: ExpandAllHeaderRenderer,
    headerComponentParams: {
      shouldExpand: (node: any) => node.data?.rowType === 'HEADING'
    },
    enableCellChangeFlash: false,
    cellDataType: 'text',
    width: 300,
    pinned: 'left',
    cellRendererSelector: params => {
      // Select the appropriate group cell component based on row type
      let component: React.ComponentType<any>

      switch (params.data?.rowType) {
        case FinancialRowType.HEADING:
          component = HeadingGroupCell
          break
        case FinancialRowType.ACCOUNT:
          component = AccountGroupCell
          break
        case FinancialRowType.DIMENSION:
          component = DimensionGroupCell
          break
        case FinancialRowType.DIMENSION_GROUP:
          component = DimensionGroupGroupCell
          break
        case FinancialRowType.INVESTMENT_MENU:
          component = InvestmentMenuGroupCell
          break
        case FinancialRowType.INVESTMENT:
          component = RowActionsGroupCell // Investment data rows with edit/delete actions
          break
        case FinancialRowType.AMENDMENT_MENU:
          component = AmendmentMenuGroupCell
          break
        case FinancialRowType.AMENDMENT:
          component = RowActionsGroupCell // Amendment data rows with edit/delete actions
          break
        case FinancialRowType.COUNTER_AMENDMENT_MENU:
          component = HeadingGroupCell // Counter amendment menu uses heading style
          break
        case FinancialRowType.REALLOCATION_MENU:
          component = HeadingGroupCell // Reallocation menu uses heading style
          break
        case FinancialRowType.REALLOCATION:
          component = AccountGroupCell // Reallocation data rows use account style
          break
        default:
          component = AccountGroupCell
      }

      return {
        component,
        params: {
          ...(onRowAction && { onRowAction })
          // Note: showCalculated, showInput, and isEditMode come from params.context (grid context)
        }
      }
    },
    wrapText: true,
    cellClassRules: getBaseRowTypeCellClassRules(),
    mainMenuItems: params => {
      const expandAllHeadingsMenuItem: MenuItemDef = {
        name: localeText?.expandAll || AG_GRID_LOCALE_FI.expandAll,
        action: () => {
          params.api.forEachNode(node => {
            if (node.data?.rowType === 'HEADING') {
              params.api.setRowNodeExpanded(node, true)
            }
          })
        },
        icon: '<i class="fa fa-angle-double-down" aria-hidden="true"></i>'
      }

      const collapseAllHeadingsMenuItem: MenuItemDef = {
        name: localeText?.collapseAll || AG_GRID_LOCALE_FI.collapseAll,
        action: () => {
          params.api.forEachNode(node => {
            if (node.data?.rowType === 'HEADING') {
              params.api.setRowNodeExpanded(node, false)
            }
          })
        },
        icon: '<i class="fa fa-angle-double-up" aria-hidden="true"></i>'
      }

      return [expandAllHeadingsMenuItem, collapseAllHeadingsMenuItem, 'separator', 'autoSizeThis', 'autoSizeAll']
    }
  }
}

/**
 * Creates hidden columns for Excel export (account code and dimension)
 */
export const createHiddenExportColumns = (): ColDef<EnhancedStatementRow>[] => {
  return [
    // Account code column (hidden in UI, visible in Excel export)
    {
      colId: 'accountCode',
      headerName: i18next.t('global:account'),
      cellDataType: 'text',
      width: 100,
      hide: true,
      valueGetter: params => {
        return params.data?._sourceAccountCode ?? ''
      },
      cellClassRules: getBaseRowTypeCellClassRules()
    },
    // Dimension column (hidden in UI, visible in Excel export)
    {
      colId: 'dimension',
      headerName: i18next.t('global:dimension'),
      cellDataType: 'text',
      width: 150,
      hide: true,
      valueGetter: params => {
        if (params.data?.rowType === 'DIMENSION') {
          return params.data?.dimensionName ?? ''
        }
        return ''
      },
      cellClassRules: getBaseRowTypeCellClassRules()
    }
  ]
}

/**
 * Build NumberFormatConfig for statement rows.
 * Reference-method budget rows → percentage, decimals still come from the
 * user's toolbar config (thousands/millions scaling never applies to them).
 * Everything else → absolute, using the user's toolbar config directly.
 */
export const buildStatementNumberFormat = (
  data: EnhancedStatementRow | undefined,
  column: { dataType?: string } | undefined,
  showInput: boolean,
  baseNumberFormat?: NumberFormatConfig
): { numberFormat: NumberFormatConfig; isPercentage: boolean } => {
  const isBudgetColumn = column?.dataType === 'budget' || column?.dataType === 'forecast'
  const isReferenceMethod = data?.budgetingMethod?.method === 'reference'

  if (isBudgetColumn && isReferenceMethod && showInput) {
    // Row-specific percentage override, merged on top of the user's base
    // config. Decimals still come from the user's toolbar choice (2 is only
    // the fallback when no base config was supplied at all). displayScale is
    // explicitly stripped: thousands/millions scaling must never apply to
    // percentage cells, regardless of the toolbar's scale setting.
    return {
      numberFormat: { decimals: 2, ...baseNumberFormat, displayScale: undefined },
      isPercentage: true
    }
  }

  // Normal case: the user's config applies directly, with decimals:0 as the
  // fallback only when nothing has been chosen yet.
  return { numberFormat: { decimals: 0, ...baseNumberFormat }, isPercentage: false }
}

/**
 * Formats the "calculated" side of a dual-value cell. Unlike the input side,
 * this is always the absolute computed amount — a reference-method row's
 * calculated value is the currency result of applying its % input, never a
 * percentage itself — so there's no showInput/budgeting-method branching to
 * do here, just the toolbar's own decimals/scale.
 */
export const statementCalculatedValueFormatter = (value: number, baseNumberFormat?: NumberFormatConfig): string =>
  formatVariableValue(value, { decimals: 0, ...baseNumberFormat }, VariableType.absolute)

export const statementValueFormatter = (params: ValueFormatterParams<EnhancedStatementRow>) => {
  if (params.value === undefined || params.value === null) return ''

  const columnContext = (params.colDef as any)?.context
  const column = columnContext?.column
  const showInput = params.context?.showInput ?? false

  const { numberFormat, isPercentage } = buildStatementNumberFormat(
    params.data,
    column,
    showInput,
    params.context?.numberFormat
  )
  // Statement columns have no ratio concept and never pass currency, so the
  // absolute/percentage split alone is enough to drive formatting here.
  const variableType = isPercentage ? VariableType.percentage : VariableType.absolute
  return formatVariableValue(params.value, numberFormat, variableType)
}

/**
 * Common cell class rules for statement columns (decimal formatting)
 */
export const getDecimalCellClassRules = () => ({
  ...getBaseRowTypeCellClassRules(),
  decimal0: (params: CellClassParams<EnhancedStatementRow>) => {
    const { value } = params
    return value === undefined || value === null || Number.isInteger(value)
  }
})

/**
 * Common value getter for statement columns
 */
export const createStatementValueGetter = (column: StatementColumn) => {
  return (params: ValueGetterParams<EnhancedStatementRow>) => {
    return extractDataValue(params.data, column)
  }
}

/**
 * Base column properties shared across all statement-based tables
 * Minimal props to avoid unnecessary re-renders
 */
export const getBaseColumnProps = (column: StatementColumn) => {
  return {
    headerName: column.label,
    minWidth: 90,
    flex: 1,
    cellDataType: 'number' as const,
    context: { column },
    headerComponent: HeaderCellRenderer,
    headerComponentParams: {
      columnData: column
    },
    cellStyle: () => ({ textAlign: 'right' as const })
  }
}

/**
 * Common base properties for financial statement column definitions
 * Includes formatting and value getters specific to financial statements
 */
export const getCommonColumnProps = (column: StatementColumn) => {
  return {
    ...getBaseColumnProps(column),
    valueFormatter: statementValueFormatter,
    cellClassRules: getDecimalCellClassRules(),
    valueGetter: createStatementValueGetter(column)
  }
}

/**
 * Check if a row's budget cells are directly editable in the grid.
 *
 * A row is editable when ALL conditions are met:
 * 1. Row type is ACCOUNT, OR row type is DIMENSION with source account
 * 2. Budgeting method is NOT 'driver' (driver-based budgets are calculated, not editable)
 */
export const isRowEditable = (data: EnhancedStatementRow | undefined): boolean => {
  if (!data) return false

  // Requirement 1: Row must be budgetable (ACCOUNT or DIMENSION with source)
  const isRowBudgetable = data.rowType === 'ACCOUNT' || (data.rowType === 'DIMENSION' && !!data._sourceAccountCode)

  if (!isRowBudgetable) return false

  // Requirement 2: Budgeting method must NOT be 'driver' (driver-based = calculated, not editable)
  if (data.budgetingMethod?.method === 'driver') return false

  return true
}

/**
 * Check if a row should use dual value display (showing both calculated and input values).
 * This is the single source of truth for determining dual value display across:
 * - Row height calculation
 * - Cell renderers
 * - Auto group column
 */
export const shouldUseDualValueDisplay = (params: {
  data?: EnhancedStatementRow
  context?: { showCalculated?: boolean; showInput?: boolean }
}): boolean => {
  const showCalculated = params.context?.showCalculated ?? true
  const showInput = params.context?.showInput ?? false

  // Must be showing both calculated and input values
  if (!showCalculated || !showInput) return false

  // Row must be directly editable (excludes driver-based rows)
  return isRowEditable(params.data)
}

/**
 * Get base cell classes (className + voucher-available)
 */
export const getBaseCellClasses = (
  params: CellClassParams<EnhancedStatementRow>,
  column: StatementColumn,
  accountingSoftware?: TaskApplication
): string[] => {
  const classes = column.className ? [column.className] : []

  // Add voucher-available class if vouchers are available
  if (isVoucherAvailable(params.data, column, accountingSoftware)) {
    classes.push('voucher-available')
  }

  return classes
}
